import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { ShoppingBasket } from 'lucide-react';

import { foodInputSchema, normalizeBarcode, searchFoods } from '@loggy/shared';
import type { Food, FoodBasis, FoodDraft, FoodInput, FoodOrigin, NutrientsPer100, Portion } from '@loggy/shared';

import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Heading,
  Inline,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
  Textarea,
  type AlertVariant,
} from '../design/index.js';
import {
  DuplicateBarcodeError,
  createNodeFood,
  deleteNodeFood,
  fetchNodeFoods,
  lookupFoodBarcode,
  updateNodeFood,
} from '../api.js';
import { BarcodeScanner } from './BarcodeScanner.js';
import styles from './FoodLibrary.module.css';

export interface FoodLibraryProps {
  nodeId: string;
  /** True when viewing someone else's dashboard. */
  readOnly: boolean;
}

type ListState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; foods: Food[] };

/** A short contextual note shown above the form — why it opened prefilled the
 *  way it did, e.g. "filled in from Open Food Facts". */
type FormBanner = { variant: AlertVariant; text: string };

/** What a scan or a search-box "add by hand" hands the create form to start
 *  from. Every field optional — unlike `FoodDraft`, this also covers the
 *  barcode-only case (Open Food Facts came back empty) and the plain "start
 *  from nothing" case. */
interface FoodFormPrefill {
  name?: string;
  brand?: string;
  barcode?: string;
  imageUrl?: string;
  basis?: FoodBasis;
  nutrientsPer100?: Partial<NutrientsPer100>;
  portions?: Portion[];
  origin?: FoodOrigin;
}

/** Which row, if any, is being added or edited. Only one at a time, same
 *  reasoning as `LifeEvents`. */
type FormState =
  | { kind: 'closed' }
  | { kind: 'create'; prefill?: FoodFormPrefill; banner?: FormBanner }
  | { kind: 'edit'; food: Food; banner?: FormBanner };

/** Which row, if any, is mid-delete. Same one-at-a-time reasoning as `LifeEvents`. */
type DeleteState = { id: string; kind: 'confirm' | 'deleting' } | null;

type ScanState = 'closed' | 'scanning' | 'looking-up';

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/** Turns what a person typed into a number, accepting a comma as the decimal
 *  separator — Dutch pack labels use one. An empty or unparsable field comes
 *  back `undefined`, which is what an omitted optional value looks like. */
function parseOptionalNumber(raw: string | undefined): number | undefined {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return undefined;
  const value = Number(trimmed.replace(',', '.'));
  return Number.isFinite(value) ? value : undefined;
}

function formatNumberInput(value: number | undefined): string {
  return value === undefined ? '' : String(value);
}

/** Every "grams (or millilitres) per 100" figure a food can carry, in the
 *  order the form asks for them. Driving both the fields and the payload
 *  from one list is what keeps the two from drifting apart. */
const NUTRIENT_FIELDS: { key: keyof NutrientsPer100; label: string; unit: string; required?: boolean }[] = [
  { key: 'energyKcal', label: 'Energy', unit: 'kcal', required: true },
  { key: 'protein', label: 'Protein', unit: 'g' },
  { key: 'carbohydrates', label: 'Carbohydrates', unit: 'g' },
  { key: 'sugars', label: 'Of which sugars', unit: 'g' },
  { key: 'fat', label: 'Fat', unit: 'g' },
  { key: 'saturatedFat', label: 'Of which saturated', unit: 'g' },
  { key: 'fibre', label: 'Fibre', unit: 'g' },
  { key: 'salt', label: 'Salt', unit: 'g' },
];

const MAX_PORTIONS = 20;

/** True when the owner typed something that is not a number at all — "3,5 g",
 *  "abc". Such a field must be refused, not read as empty: dropping a figure
 *  the owner typed would store a food without it and say nothing. */
function isUnparsableNumber(raw: string | undefined): boolean {
  return (raw ?? '').trim() !== '' && parseOptionalNumber(raw) === undefined;
}

/**
 * A schema issue in the owner's terms: which field, then what is wrong with it.
 *
 * zod's own text never names the field — a form that answers "Required" or
 * "Number must be less than or equal to 100" leaves the owner hunting for
 * which of eight figures it means.
 */
function describeIssue(issue: { path: (string | number)[]; message: string }): string {
  const [first, second] = issue.path;
  let field: string | null = null;
  if (first === 'name') field = 'Name';
  else if (first === 'brand') field = 'Brand';
  else if (first === 'note') field = 'Note';
  else if (first === 'nutrientsPer100') {
    field = NUTRIENT_FIELDS.find((candidate) => candidate.key === second)?.label ?? 'A nutrient';
  } else if (first === 'portions') {
    field = typeof second === 'number' ? `Portion ${second + 1}` : 'Portions';
  }
  return field === null ? issue.message : `${field}: ${issue.message.charAt(0).toLowerCase()}${issue.message.slice(1)}`;
}

/** A product photo, 48x48, or a neutral placeholder mark when the food has
 *  none — never a broken-image icon, which is what an invented URL would
 *  eventually show instead. */
function FoodThumbnail({ name, imageUrl }: { name: string; imageUrl?: string }) {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt={name}
        loading="lazy"
        referrerPolicy="no-referrer"
        className={styles.thumbnail}
      />
    );
  }
  return (
    <div className={styles.thumbnailPlaceholder} aria-hidden="true">
      <ShoppingBasket size={20} />
    </div>
  );
}

/**
 * Add-a-food / edit-a-food, in one form — same shape as `LifeEvents`'
 * `EventForm`. Creating from a barcode scan and creating from scratch are the
 * same form with a different `prefill`; editing is the same form again with
 * `food` set, which is also what makes it a full replace rather than a patch
 * of just the changed fields.
 */
function FoodForm({
  food,
  prefill,
  banner,
  save,
  onSaved,
  onCancel,
  onOpenExisting,
}: {
  food: Food | null;
  prefill?: FoodFormPrefill;
  banner?: FormBanner;
  save: (input: FoodInput) => Promise<Food>;
  onSaved: (food: Food) => void;
  onCancel: () => void;
  onOpenExisting: (foodId: string) => void;
}) {
  const isEditing = food != null;
  const startingNutrients = food?.nutrientsPer100 ?? prefill?.nutrientsPer100;
  const startingPortions = food?.portions ?? prefill?.portions ?? [];
  const imageUrl = food?.imageUrl ?? prefill?.imageUrl;

  const [name, setName] = useState(food?.name ?? prefill?.name ?? '');
  const [brand, setBrand] = useState(food?.brand ?? prefill?.brand ?? '');
  const [barcodeText, setBarcodeText] = useState(food?.barcode ?? prefill?.barcode ?? '');
  const [basis, setBasis] = useState<FoodBasis>(food?.basis ?? prefill?.basis ?? 'g');
  const [nutrientValues, setNutrientValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(NUTRIENT_FIELDS.map((field) => [field.key, formatNumberInput(startingNutrients?.[field.key])])),
  );
  const [portions, setPortions] = useState(
    startingPortions.map((portion) => ({ label: portion.label, amount: String(portion.amount) })),
  );
  const [note, setNote] = useState(food?.note ?? '');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [duplicate, setDuplicate] = useState<DuplicateBarcodeError | null>(null);

  function updatePortion(index: number, key: 'label' | 'amount', value: string) {
    setPortions((prev) => prev.map((p, i) => (i === index ? { ...p, [key]: value } : p)));
  }

  function addPortion() {
    setPortions((prev) => (prev.length >= MAX_PORTIONS ? prev : [...prev, { label: '', amount: '' }]));
  }

  function removePortion(index: number) {
    setPortions((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setSubmitError(null);
    setDuplicate(null);

    const trimmedName = name.trim();

    let barcode: string | undefined;
    if (barcodeText.trim()) {
      const normalized = normalizeBarcode(barcodeText.trim());
      if (!normalized) {
        setSubmitError('That is not a valid barcode — check the digits.');
        return;
      }
      barcode = normalized;
    }

    const unparsable = NUTRIENT_FIELDS.find((field) => isUnparsableNumber(nutrientValues[field.key]));
    if (unparsable) {
      setSubmitError(`${unparsable.label}: that is not a number.`);
      return;
    }
    const unparsablePortion = portions.findIndex((portion) => isUnparsableNumber(portion.amount));
    if (unparsablePortion !== -1) {
      setSubmitError(`Portion ${unparsablePortion + 1}: that amount is not a number.`);
      return;
    }

    const nutrientsPer100: Partial<NutrientsPer100> = {};
    for (const field of NUTRIENT_FIELDS) {
      const parsed = parseOptionalNumber(nutrientValues[field.key]);
      if (parsed !== undefined) nutrientsPer100[field.key] = parsed;
    }

    // A row left entirely blank is not a portion at all; a half-filled row
    // (a label with no amount, or the reverse) is left for the schema below
    // to reject, rather than silently dropped — that is the owner having
    // started typing something the form should not throw away quietly.
    const portionInputs = portions
      .filter((portion) => portion.label.trim() || portion.amount.trim())
      .map((portion) => ({
        label: portion.label.trim(),
        amount: parseOptionalNumber(portion.amount) as number,
      }));

    const candidate: Record<string, unknown> = {
      name: trimmedName,
      basis,
      nutrientsPer100,
      portions: portionInputs,
      origin: food?.origin ?? prefill?.origin ?? 'manual',
    };
    if (brand.trim()) candidate.brand = brand.trim();
    if (barcode) candidate.barcode = barcode;
    if (imageUrl) candidate.imageUrl = imageUrl;
    if (note.trim()) candidate.note = note.trim();

    const result = foodInputSchema.safeParse(candidate);
    if (!result.success) {
      const issue = result.error.issues[0];
      setSubmitError(issue ? describeIssue(issue) : 'Check the form and try again.');
      return;
    }

    setSubmitting(true);
    try {
      const saved = await save(result.data);
      onSaved(saved);
    } catch (err) {
      setSubmitting(false);
      if (err instanceof DuplicateBarcodeError) {
        setDuplicate(err);
      } else {
        setSubmitError(errorMessage(err, 'Could not save this food.'));
      }
    }
  }

  return (
    <Card data-testid={isEditing ? 'food-form-edit' : 'food-form-create'}>
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap={5}>
          <Heading level={4} size="md">{isEditing ? 'Edit food' : 'Add a food'}</Heading>

          {banner && (
            <Alert variant={banner.variant} data-testid="food-form-banner">
              {banner.text}
            </Alert>
          )}

          {imageUrl && (
            <img
              src={imageUrl}
              alt={name || 'Product photo'}
              referrerPolicy="no-referrer"
              className={styles.formPhoto}
            />
          )}

          <Field label="Name" required>
            <Input
              data-testid="food-form-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Halfvolle melk"
              disabled={submitting}
              required
            />
          </Field>

          <Field label="Brand (optional)">
            <Input
              data-testid="food-form-brand"
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              disabled={submitting}
            />
          </Field>

          <Field label="Barcode (optional)">
            <Input
              data-testid="food-form-barcode"
              value={barcodeText}
              onChange={(e) => setBarcodeText(e.target.value)}
              placeholder="Scan or type the number"
              inputMode="numeric"
              disabled={submitting}
            />
          </Field>

          <Field label="Measured in">
            <Select
              data-testid="food-form-basis"
              value={basis}
              onChange={(e) => setBasis(e.target.value as FoodBasis)}
              disabled={submitting}
            >
              <option value="g">Grams (g)</option>
              <option value="ml">Millilitres (ml)</option>
            </Select>
          </Field>

          <Stack gap={4}>
            {NUTRIENT_FIELDS.map((field) => (
              <Field
                key={field.key}
                label={`${field.label} (${field.unit} per 100 ${basis})`}
                required={field.required}
                hint={field.key === 'energyKcal' ? 'If the pack only gives kJ, divide by 4.184.' : undefined}
              >
                <Input
                  data-testid={`food-form-${field.key}`}
                  value={nutrientValues[field.key] ?? ''}
                  onChange={(e) =>
                    setNutrientValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                  }
                  inputMode="decimal"
                  placeholder="e.g. 3,5"
                  disabled={submitting}
                  required={field.required}
                />
              </Field>
            ))}
          </Stack>

          <Stack gap={2}>
            <Text size="sm" weight="medium">Portions (optional)</Text>
            {portions.map((portion, index) => (
              <Inline gap={2} align="center" wrap key={index} data-testid="food-form-portion-row">
                <Input
                  aria-label="Portion label"
                  className={styles.portionLabel}
                  value={portion.label}
                  placeholder="e.g. 1 slice"
                  onChange={(e) => updatePortion(index, 'label', e.target.value)}
                  disabled={submitting}
                />
                <Input
                  aria-label={`Amount in ${basis}`}
                  className={styles.portionAmount}
                  value={portion.amount}
                  placeholder={basis}
                  inputMode="decimal"
                  onChange={(e) => updatePortion(index, 'amount', e.target.value)}
                  disabled={submitting}
                />
                <Text size="sm" color="muted">{basis}</Text>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removePortion(index)}
                  disabled={submitting}
                >
                  Remove
                </Button>
              </Inline>
            ))}
            {portions.length < MAX_PORTIONS && (
              <Button type="button" variant="secondary" size="sm" onClick={addPortion} disabled={submitting}>
                Add a portion
              </Button>
            )}
          </Stack>

          <Field label="Note (optional)">
            <Textarea
              data-testid="food-form-note"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              disabled={submitting}
            />
          </Field>

          {submitError && (
            <Alert variant="error" title="Couldn't save this food" data-testid="food-form-error">
              {submitError}
            </Alert>
          )}

          {duplicate && (
            <Alert variant="error" title="Couldn't save this food" data-testid="food-form-duplicate">
              {duplicate.message}{' '}
              {duplicate.foodId && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => onOpenExisting(duplicate.foodId)}
                >
                  Edit existing food
                </Button>
              )}
            </Alert>
          )}

          <Inline gap={3} align="center" wrap>
            <Button type="submit" size="sm" data-testid="food-form-submit" disabled={submitting}>
              {submitting ? 'Saving…' : isEditing ? 'Save changes' : 'Add food'}
            </Button>
            {submitting && <Spinner size="sm" aria-label="Saving this food" />}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="food-form-cancel"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </Button>
          </Inline>
        </Stack>
      </form>
    </Card>
  );
}

/** One food as a read row: photo, name, brand, energy, and its portions as
 *  badges — plus delete, entirely absent rather than disabled when
 *  `readOnly`. The owner opens the edit form by activating the row itself,
 *  the same clickable-card pattern `NodeRail` uses for the orbit list. */
function FoodRow({
  food,
  readOnly,
  deleteState,
  onOpen,
  onDeleteRequested,
  onDeleteConfirmed,
  onDeleteCancelled,
  deleteError,
}: {
  food: Food;
  readOnly: boolean;
  deleteState: DeleteState;
  onOpen: () => void;
  onDeleteRequested: () => void;
  onDeleteConfirmed: () => void;
  onDeleteCancelled: () => void;
  deleteError: string | null;
}) {
  const confirming = deleteState?.id === food.id && deleteState.kind === 'confirm';
  const deleting = deleteState?.id === food.id && deleteState.kind === 'deleting';
  const rowIsOpenable = !readOnly && !confirming && !deleting;
  const unit = food.basis;

  return (
    <Card
      data-testid="food-row"
      role={rowIsOpenable ? 'button' : undefined}
      tabIndex={rowIsOpenable ? 0 : undefined}
      onClick={rowIsOpenable ? onOpen : undefined}
      onKeyDown={
        rowIsOpenable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
    >
      <Stack gap={2}>
        <Inline gap={3} align="center" wrap className={styles.rowHead}>
          <Inline gap={3} align="center" className={styles.rowMain}>
            <FoodThumbnail name={food.name} imageUrl={food.imageUrl} />
            <Stack gap={1} className={styles.rowText}>
              <Text weight="medium">{food.name}</Text>
              {food.brand && <Text size="sm" color="muted">{food.brand}</Text>}
              <Inline gap={2} align="center" wrap>
                <Text size="sm" tabular data-testid="food-row-energy">
                  {Math.round(food.nutrientsPer100.energyKcal)} kcal / 100 {unit}
                </Text>
                {food.portions.map((portion, index) => (
                  <Badge key={index} variant="neutral">
                    {portion.label} · {portion.amount} {unit}
                  </Badge>
                ))}
              </Inline>
            </Stack>
          </Inline>

          {!readOnly && !confirming && !deleting && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              data-testid="food-row-delete-button"
              onClick={(event) => {
                event.stopPropagation();
                onDeleteRequested();
              }}
            >
              Delete
            </Button>
          )}
        </Inline>

        {!readOnly && (confirming || deleting) && (
          <Inline
            gap={3}
            align="center"
            wrap
            data-testid="food-row-delete-confirm"
            onClick={(event) => event.stopPropagation()}
          >
            <Text size="sm" color="secondary">
              Delete &ldquo;{food.name}&rdquo;? This can&rsquo;t be undone.
            </Text>
            <Button
              type="button"
              variant="danger"
              size="sm"
              data-testid="food-row-delete-confirm-button"
              onClick={onDeleteConfirmed}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Yes, delete'}
            </Button>
            <Button type="button" variant="ghost" size="sm" disabled={deleting} onClick={onDeleteCancelled}>
              Cancel
            </Button>
          </Inline>
        )}

        {!readOnly && deleteState?.id === food.id && deleteError && (
          <Alert variant="error" title="Couldn't delete this food" data-testid="food-row-delete-error">
            {deleteError}
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

/**
 * The owner's food library: search what is already in it, add a food by
 * scanning a barcode or typing it in once, edit or delete an entry.
 *
 * Somebody else mounts this inside the node-detail screen; this component
 * only ever talks to `nodeId`'s own foods.
 */
export function FoodLibrary({ nodeId, readOnly }: FoodLibraryProps) {
  const [listState, setListState] = useState<ListState>({ kind: 'loading' });
  const [query, setQuery] = useState('');
  const [formState, setFormState] = useState<FormState>({ kind: 'closed' });
  const [deleteState, setDeleteState] = useState<DeleteState>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [scanState, setScanState] = useState<ScanState>('closed');
  const [lookupError, setLookupError] = useState<{ barcode: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setListState({ kind: 'loading' });
    try {
      const foods = await fetchNodeFoods(nodeId);
      setListState({ kind: 'loaded', foods });
    } catch (err) {
      setListState({ kind: 'error', message: errorMessage(err, 'Could not load this food library.') });
    }
  }, [nodeId]);

  useEffect(() => {
    void load();
    // Closing whatever was open when the node itself changes — a form or a
    // delete confirmation left over from a different node's foods would be
    // editing the wrong thing.
    setFormState({ kind: 'closed' });
    setDeleteState(null);
    setScanState('closed');
    setLookupError(null);
    setQuery('');
  }, [load]);

  function upsert(saved: Food) {
    setListState((prev) => {
      if (prev.kind !== 'loaded') return prev;
      const withoutSaved = prev.foods.filter((f) => f.id !== saved.id);
      return { kind: 'loaded', foods: [...withoutSaved, saved].sort((a, b) => a.name.localeCompare(b.name)) };
    });
    setFormState({ kind: 'closed' });
  }

  async function handleDelete(foodId: string) {
    setDeleteState({ id: foodId, kind: 'deleting' });
    setDeleteError(null);
    try {
      await deleteNodeFood(nodeId, foodId);
      setListState((prev) =>
        prev.kind === 'loaded' ? { kind: 'loaded', foods: prev.foods.filter((f) => f.id !== foodId) } : prev,
      );
      setDeleteState(null);
    } catch (err) {
      setDeleteState({ id: foodId, kind: 'confirm' });
      setDeleteError(errorMessage(err, 'Could not delete this food.'));
    }
  }

  /**
   * Every way a form opens goes through here, so none of them can leave the
   * camera running behind it — clicking a row while the scanner is up used
   * to open the form with the camera still on.
   */
  function openForm(state: Exclude<FormState, { kind: 'closed' }>) {
    setScanState('closed');
    setFormState(state);
  }

  /**
   * Opens the food a 409 named. It may not be in the loaded list — added from
   * another tab since — so a miss reloads the list rather than doing nothing.
   */
  async function openExisting(foodId: string, note?: string) {
    const banner = note ? ({ variant: 'info', text: note } as const) : undefined;
    const loaded = listState.kind === 'loaded' ? listState.foods.find((f) => f.id === foodId) : undefined;
    if (loaded) {
      openForm({ kind: 'edit', food: loaded, banner });
      return;
    }
    try {
      const foods = await fetchNodeFoods(nodeId);
      setListState({ kind: 'loaded', foods });
      const fetched = foods.find((f) => f.id === foodId);
      if (fetched) openForm({ kind: 'edit', food: fetched, banner });
    } catch (err) {
      setListState({ kind: 'error', message: errorMessage(err, 'Could not load this food library.') });
    }
  }

  /**
   * A code came off the scanner, already normalised. A match already in the
   * library opens straight into its edit form; otherwise it is a lookup
   * against Open Food Facts, via our own API — never the network directly,
   * see CLAUDE.md.
   */
  async function handleScanned(barcode: string) {
    setScanState('closed');
    setLookupError(null);

    if (listState.kind === 'loaded') {
      const existing = listState.foods.find((f) => f.barcode === barcode);
      if (existing) {
        openForm({ kind: 'edit', food: existing, banner: { variant: 'info', text: 'Already in your library.' } });
        return;
      }
    }

    setScanState('looking-up');
    try {
      const draft: FoodDraft | null = await lookupFoodBarcode(nodeId, barcode);
      if (draft) {
        openForm({
          kind: 'create',
          prefill: draft,
          banner: {
            variant: 'info',
            text: 'Filled in from Open Food Facts — check the figures against the pack before saving.',
          },
        });
      } else {
        openForm({
          kind: 'create',
          prefill: { barcode },
          banner: { variant: 'info', text: 'Not in Open Food Facts yet. Fill it in once and it’s in your library for good.' },
        });
      }
    } catch (err) {
      // The server found it in this library — a scan made while the list was
      // still loading, or a food added from another tab since.
      if (err instanceof DuplicateBarcodeError) {
        await openExisting(err.foodId, 'Already in your library.');
        return;
      }
      setLookupError({
        barcode,
        message: errorMessage(err, 'Open Food Facts did not answer. Try again, or fill it in by hand.'),
      });
    } finally {
      setScanState('closed');
    }
  }

  const formOpen = formState.kind !== 'closed';
  const foods = listState.kind === 'loaded' ? listState.foods : [];
  const matches = listState.kind === 'loaded' ? searchFoods(listState.foods, query) : [];
  // The food being edited stays on screen whatever the search says. The form
  // renders in its row, and an edit opened by a scan or a 409 while a search
  // is typed would otherwise open where nobody can see it — with the Scan and
  // Add buttons hidden for as long as it is "open", the screen looked stuck.
  const editing = formState.kind === 'edit' ? formState.food : null;
  const filteredFoods =
    editing && !matches.some((f) => f.id === editing.id) ? [editing, ...matches] : matches;
  const trimmedQuery = query.trim();

  return (
    <Stack gap={4} className={styles.page} data-testid="food-library">
      <Inline gap={3} align="center" wrap>
        <Heading level={3} size="lg">Food library</Heading>
      </Inline>

      <Inline gap={3} align="center" wrap>
        <Input
          className={styles.search}
          data-testid="food-library-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search your foods"
          aria-label="Search your foods"
        />
        {!readOnly && !formOpen && scanState === 'closed' && (
          <>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              data-testid="food-library-scan"
              onClick={() => setScanState('scanning')}
            >
              Scan barcode
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              data-testid="food-library-add"
              onClick={() => openForm({ kind: 'create' })}
            >
              Add by hand
            </Button>
          </>
        )}
      </Inline>

      {scanState === 'scanning' && (
        <BarcodeScanner onDetected={(barcode) => void handleScanned(barcode)} onCancel={() => setScanState('closed')} />
      )}

      {scanState === 'looking-up' && (
        <Inline gap={3} align="center" data-testid="food-library-looking-up">
          <Spinner size="sm" aria-label="Looking up this barcode" />
          <Text size="sm" color="muted">Looking it up…</Text>
        </Inline>
      )}

      {lookupError && (
        <Alert variant="error" title="Couldn't look up that barcode" data-testid="food-library-lookup-error">
          {lookupError.message}{' '}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              openForm({ kind: 'create', prefill: { barcode: lookupError.barcode } });
              setLookupError(null);
            }}
          >
            Fill it in by hand
          </Button>
        </Alert>
      )}

      {!readOnly && formState.kind === 'create' && (
        <FoodForm
          food={null}
          prefill={formState.prefill}
          banner={formState.banner}
          save={(input) => createNodeFood(nodeId, input)}
          onSaved={upsert}
          onCancel={() => setFormState({ kind: 'closed' })}
          onOpenExisting={(foodId) => void openExisting(foodId)}
        />
      )}

      {listState.kind === 'loading' && (
        <Inline gap={3} align="center" data-testid="food-library-loading">
          <Spinner size="sm" aria-label="Loading your food library" />
          <Text size="sm" color="muted">Loading…</Text>
        </Inline>
      )}

      {listState.kind === 'error' && (
        <Alert variant="error" title="Couldn't load this food library" data-testid="food-library-error">
          {listState.message}
        </Alert>
      )}

      {listState.kind === 'loaded' && foods.length === 0 && (
        <Text size="sm" color="muted" data-testid="food-library-empty">
          Nothing here yet. Scan a barcode or add a food by hand — each product only ever needs to be entered once.
        </Text>
      )}

      {listState.kind === 'loaded' && foods.length > 0 && filteredFoods.length === 0 && (
        <Stack gap={2} data-testid="food-library-no-match">
          <Text size="sm" color="muted">No matches for &ldquo;{trimmedQuery}&rdquo;.</Text>
          {!readOnly && trimmedQuery && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => openForm({ kind: 'create', prefill: { name: trimmedQuery } })}
            >
              Add &ldquo;{trimmedQuery}&rdquo; by hand
            </Button>
          )}
        </Stack>
      )}

      {listState.kind === 'loaded' && filteredFoods.length > 0 && (
        <Stack gap={3} data-testid="food-library-list">
          {filteredFoods.map((food) =>
            !readOnly && formState.kind === 'edit' && formState.food.id === food.id ? (
              <FoodForm
                key={food.id}
                food={formState.food}
                banner={formState.banner}
                save={(input) => updateNodeFood(nodeId, food.id, input)}
                onSaved={upsert}
                onCancel={() => setFormState({ kind: 'closed' })}
                onOpenExisting={(foodId) => void openExisting(foodId)}
              />
            ) : (
              <FoodRow
                key={food.id}
                food={food}
                readOnly={readOnly}
                deleteState={deleteState}
                onOpen={() => openForm({ kind: 'edit', food })}
                onDeleteRequested={() => {
                  setDeleteError(null);
                  setDeleteState({ id: food.id, kind: 'confirm' });
                }}
                onDeleteConfirmed={() => void handleDelete(food.id)}
                onDeleteCancelled={() => setDeleteState(null)}
                deleteError={deleteState?.id === food.id ? deleteError : null}
              />
            ),
          )}
        </Stack>
      )}

      {/* The Open Database License asks that data from Open Food Facts is
          credited wherever it is shown. Always present rather than only
          beside looked-up foods: the library as a whole is built on it, and a
          credit that appears and disappears reads as an afterthought. */}
      <Text size="xs" color="muted" className={styles.credit} data-testid="food-library-credit">
        Product data and photos from{' '}
        <a href="https://world.openfoodfacts.org" target="_blank" rel="noreferrer">
          Open Food Facts
        </a>
        , available under the{' '}
        <a href="https://opendatacommons.org/licenses/odbl/1-0/" target="_blank" rel="noreferrer">
          Open Database License
        </a>
        .
      </Text>
    </Stack>
  );
}
